// Client-side CSV/JSON export for admin list pages. Rows are fetched via the
// page's existing list API (honouring the active search/filters/date-range), so
// no backend export endpoints are needed.
import type { ListParams, PaginatedResult } from "../types/api";

export interface ExportField<T> {
  label: string;
  value: (row: T) => string | number | boolean | null | undefined;
}

// Page through a list endpoint collecting every row matching `params`. Capped at
// maxPages * pageSize so a runaway dataset can't lock the browser.
export async function collectAll<T>(
  fetcher: (p: ListParams) => Promise<PaginatedResult<T>>,
  params: ListParams = {},
  opts: { pageSize?: number; maxPages?: number } = {},
): Promise<T[]> {
  const pageSize = opts.pageSize ?? 100;
  const maxPages = opts.maxPages ?? 100;
  const out: T[] = [];
  for (let page = 1; page <= maxPages; page++) {
    const res = await fetcher({ ...params, page, limit: pageSize });
    out.push(...(res.items ?? []));
    if (page >= (res.totalPages ?? 1)) break;
  }
  return out;
}

function csvCell(v: unknown): string {
  if (v === null || v === undefined) return "";
  const s = String(v);
  // Quote when the value contains a comma, quote or newline; escape quotes.
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function toCsv<T>(rows: T[], fields: ExportField<T>[]): string {
  const header = fields.map((f) => csvCell(f.label)).join(",");
  const lines = rows.map((r) => fields.map((f) => csvCell(f.value(r))).join(","));
  return [header, ...lines].join("\r\n");
}

export function toJson<T>(rows: T[], fields: ExportField<T>[]): string {
  const mapped = rows.map((r) => {
    const o: Record<string, unknown> = {};
    for (const f of fields) o[f.label] = f.value(r) ?? null;
    return o;
  });
  return JSON.stringify(mapped, null, 2);
}

export function downloadBlob(filename: string, content: string, mime: string): void {
  const blob = new Blob([content], { type: `${mime};charset=utf-8;` });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// "drivers" → "drivers-2026-06-17.csv"
export function exportFilename(base: string, ext: string): string {
  return `${base}-${new Date().toISOString().slice(0, 10)}.${ext}`;
}
